import React, { useMemo, useContext } from "react";
import { graphql } from "gatsby";
import MicroCopy, { MicroCopyContext } from "@unum/micro-copy/";
import Footer, { Navigation, SocialLink } from "@unum/footer";
import { getClickableAction } from "./Link";
import {
  FooterFragmentFragment,
  RichTextFragmentFragment
} from "../../graphql-types";
import YouTubeLogo from "../icons/youtube-logo.inline.svg";
import LinkedInLogo from "../icons/linkedin-logo.inline.svg";
import RichText, { checkValueExists } from "./RichText";

type Props = {
  data: FooterFragmentFragment;
  footerText?: RichTextFragmentFragment;
};

// @todo: Add a migration to add the links in Site>Settings.
// https://digitaldetox.atlassian.net/browse/UNKFLM-464
const socialLinks: SocialLink[] = [
  {
    icon: LinkedInLogo,
    accessibilityLabel: "LinkedIn Logo",
    action: {
      model: "htmlLink",
      href: "https://www.linkedin.com/company/unum-uk"
    }
  },
  {
    icon: YouTubeLogo,
    accessibilityLabel: "YouTube Logo",
    action: { model: "htmlLink", href: "https://www.youtube.com/user/unumuk" }
  }
];

const mapItemsToNavigation = (
  items: FooterFragmentFragment["elements"]["items"]["value"]
): Navigation[] => {
  return items.map((item) => {
    const { label, items } = item.elements;
    return {
      label: label.value,
      links: items.value.map((link) => {
        if (
          !(
            link.__typename === "kontent_item_external_link" ||
            link.__typename === "kontent_item_internal_link"
          )
        ) {
          return null;
        }

        return {
          label: link.elements.label.value,
          action: getClickableAction(link)
        };
      })
    };
  });
};

const IntegratedFooter = ({ data, footerText }: Props) => {
  if (!data) {
    return null;
  }

  const { items } = data.elements;
  const navLinks = useMemo(() => mapItemsToNavigation(items.value), [items]);
  const getMicroCopy = useContext(MicroCopyContext);

  return (
    <Footer
      socialLinks={socialLinks}
      navLinks={navLinks}
      footer={
        <>
          <Footer.Description>
            <b>
              &copy; {new Date().getFullYear()}{" "}
              <MicroCopy path="footer.copyright" />
            </b>
          </Footer.Description>
          <Footer.Description>
            {checkValueExists(footerText?.value) ? (
              <RichText data={footerText} />
            ) : (
              getMicroCopy("footer.terms")
            )}
          </Footer.Description>
        </>
      }
    />
  );
};

export default IntegratedFooter;

export const query = graphql`
  fragment FooterFragment on kontent_item_navigation {
    elements {
      items {
        value {
          ... on kontent_item_navigation {
            id
            elements {
              label {
                value
              }
              items {
                value {
                  ...LinkFragment
                }
              }
            }
          }
        }
      }
    }
  }
`;
